import { Box, FormHelperText, FormLabel, Stack } from '@mui/material';
import { useMarkupTFunction } from '@retail/markup/i18n';
import { useController, useFormContext } from 'react-hook-form';
import { ErrorOutlineRounded } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import { useCallback, useState } from 'react';
import { DateRangeSchema } from './dateRangeSchema';

interface Props {
  excludeToDate?: boolean;
  alignment?: 'row' | 'column';
  labelAlignment?: 'row' | 'column';
  inputFieldWidth?: number;
  availableMinDate?: string;
  availableMaxDate?: string;
  todayAsDefaultFromValue?: boolean;
}

export function DatesForm({
  excludeToDate = false,
  alignment = 'row',
  labelAlignment = 'column',
  inputFieldWidth = 192,
  availableMinDate,
  availableMaxDate,
  todayAsDefaultFromValue,
}: Props) {
  const t = useMarkupTFunction();
  const { control } = useFormContext<DateRangeSchema>();

  const fromDateController = useController({ name: 'fromDate', control });
  const toDateController = useController({ name: 'toDate', control });

  const [fromDate, setFromDate] = useState<Date>(
    todayAsDefaultFromValue ? new Date() : fromDateController.field.value
  );

  const handleSetFromDate = useCallback(
    (date: Date) => {
      fromDateController.field.onChange(date);
      setFromDate(date);
    },
    [fromDateController.field]
  );

  return (
    <Stack flexDirection={alignment} gap={2}>
      <Box
        display="flex"
        flexDirection={labelAlignment}
        gap={labelAlignment === 'row' ? 0 : 1}
        alignItems={labelAlignment === 'row' ? 'center' : 'flex-start'}
        width="100%"
      >
        <FormLabel
          sx={{ fontWeight: 500, width: 150 }}
          error={!!fromDateController.fieldState.error}
          required
        >
          {t('markup.fromDate')}
        </FormLabel>

        <Stack gap={1} overflow="hidden">
          <DatePicker
            {...fromDateController.field}
            value={fromDate}
            onChange={(value) => value && handleSetFromDate(value)}
            minDate={
              availableMinDate && dayjs(availableMinDate).isAfter(new Date())
                ? new Date(availableMinDate)
                : new Date()
            }
            maxDate={
              availableMaxDate && dayjs(availableMaxDate).isAfter(new Date())
                ? new Date(availableMaxDate)
                : undefined
            }
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                sx: { ...inputSx, width: inputFieldWidth },
                helperText: undefined,
              },
            }}
          />

          {!!fromDateController.fieldState.error && (
            <Stack direction="row" spacing={1} flexWrap="nowrap" alignItems="center">
              <ErrorOutlineRounded color="warning" fontSize="small" />
              <FormHelperText sx={{ color: 'warning.main' }}>
                {fromDateController.fieldState.error.message}
              </FormHelperText>
            </Stack>
          )}
        </Stack>
      </Box>
      {!excludeToDate && (
        <Box
          display="flex"
          flexDirection={labelAlignment}
          gap={labelAlignment === 'row' ? 0 : 1}
          alignItems={labelAlignment === 'row' ? 'center' : 'flex-start'}
          width="100%"
        >
          <FormLabel sx={{ fontWeight: 500, width: 150 }}>{t('markup.toDate')}</FormLabel>
          <DatePicker
            {...toDateController.field}
            minDate={
              availableMinDate && dayjs(availableMinDate).isAfter(new Date())
                ? new Date(availableMinDate)
                : new Date()
            }
            maxDate={
              availableMaxDate && dayjs(availableMaxDate).isAfter(new Date())
                ? new Date(availableMaxDate)
                : undefined
            }
            format="dd/MM/yyyy"
            slotProps={{
              field: { clearable: true },
              textField: {
                sx: { ...inputSx, width: inputFieldWidth },
                helperText: undefined,
              },
            }}
          />
        </Box>
      )}
    </Stack>
  );
}

export const inputSx = {
  '& .MuiInputBase-root': {
    height: 40,
    borderRadius: 2,
    maxWidth: 192,
  },
  '& .MuiPickersSectionList-root': {
    paddingTop: 1,
    paddingBottom: 1,
  },
};
